import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button, Typography } from "@mui/material";
import LineGraph from "./lineGraph";
import { useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { ContinuousColorLegend } from "@mui/x-charts";
import Shit from "./Incidents";
import { academicTerms } from "./masterStructure";
import { parseMarksString, average, numOrNull } from "./marksUtils";

export default function DataTable(props) {
  const theme = useTheme();
  let filterOptions = props.filterOptions;
  let setFilterOptions = props.setFilterOptions;

  function changeFilterOptions(event) {
    setFilterOptions({
      ...filterOptions,
      [event.target.name]: event.target.value,
    });
  }

  let rowData = [];

  let headers = [];

  let termMarksAvg = props.termMarksAvg;

  for (const i in props.dataPacket) {
    if (props.indivMarks && headers.length == 0) {
      headers = termMarksAvg.filter((x) => isNaN(x));
    } else if (!props.indivMarks) {
      headers.push(i);
    }

    if (props.indivMarks) {
      rowData = termMarksAvg.filter((x) => !isNaN(x));
    } else {
      rowData.push(
        props.arrayFlipped
          ? props.studentInfo[props.studentInfo.length - props.dataPacket[i]]
          : props.studentInfo[props.dataPacket[i]]
      );
    }
  }

  const createHeader = headers.map((headerName, index) => {
    return (
      !props.hideHeader &&
      (rowData[index] || props.indivMarks) && (
        <TableCell align="center" key={headerName + index}>
          {headerName}
        </TableCell>
      )
    );
  });

  const createRowData = rowData.map((row, index) => {
    if (!row) return null;

    let parts;

    if (props.formatAsButton) {
      return (
        <TableCell align="center" key={index}>
          <Button variant="outlined" onClick={() => window.open(row)}>
            Access Here
          </Button>
        </TableCell>
      );
    } else if (props.healthTracker) {
      return <Shit studentInfo={props.studentInfo} />;
    } else if (props.insertBr) {
      parts = row.split("•").map((part, index, array) => (
        <React.Fragment key={row + index + props.studentInfo[1]}>
          {part}
          {index < array.length - 1 && (
            <>
              {index >= 0 && (
                <>
                  <br />
                  <br />
                </>
              )}
              •
            </>
          )}
        </React.Fragment>
      ));
    } else if (props.isGraph) {
      // One point per school term (T1 + T2 of every year in masterStructure), oldest
      // first, so a new school year appears on the graph as soon as it's added there —
      // no hardcoded columns or fixed-length tuples to update here.
      const xLabels = academicTerms.map((term) => term.label);

      const classmates = props.rawData.filter(
        (student) => student[5] == props.studentInfo[5],
      );

      const loadedMarks = [];
      const averageMarks = [];

      // "GS Average" / "JS Average": the report card's overall term grade, per track.
      ["GS", "JS"].forEach((track) => {
        const indexKey = track === "GS" ? "gsIndex" : "jsIndex";
        const studentByTerm = academicTerms.map((term) =>
          numOrNull(props.studentInfo[term[indexKey]]),
        );

        // Skip a track entirely if this student never had a mark in it (e.g. a
        // student who only ever took the GS track has no "JS Average" series).
        if (!studentByTerm.some((mark) => mark !== null)) return;

        loadedMarks.push({ [`${track} Average`]: studentByTerm });
        averageMarks.push({
          [`${track} Average`]: academicTerms.map(
            (term) =>
              average(
                classmates.map((student) =>
                  numOrNull(student[term[indexKey]]),
                ),
              ) || null,
          ),
        });
      });

      // Per-course marks: every course this student has ever had a mark in, across
      // every term. A course missing in some terms (new course, student switched
      // classes, mark not entered yet, ...) just shows as a gap on that course's line.
      const studentTermCourses = academicTerms.map((term) =>
        parseMarksString(props.studentInfo[term.courseIndex]),
      );

      const courseNames = new Set();
      studentTermCourses.forEach((courses) => {
        for (let i = 0; i < courses.length; i += 2) {
          if (courses[i]) courseNames.add(courses[i]);
        }
      });

      const classTermCourses = academicTerms.map((term) =>
        classmates.map((student) => parseMarksString(student[term.courseIndex])),
      );

      const markFor = (courses, courseName) => {
        const markIndex = courses.indexOf(courseName);
        return markIndex !== -1 ? numOrNull(courses[markIndex + 1]) : null;
      };

      courseNames.forEach((courseName) => {
        loadedMarks.push({
          [courseName]: studentTermCourses.map((courses) =>
            markFor(courses, courseName),
          ),
        });

        averageMarks.push({
          [courseName]: classTermCourses.map(
            (termCourses) =>
              average(
                termCourses.map((courses) => markFor(courses, courseName)),
              ) || null,
          ),
        });
      });

      const selOptions = loadedMarks.map((course) => Object.keys(course)[0]);

      let selectedIndex = 0;
      for (let i in loadedMarks) {
        if (Object.keys(loadedMarks[i])[0] == filterOptions.termSelect) {
          selectedIndex = i;
          break;
        }
      }

      const specificData = Object.values(loadedMarks[selectedIndex] || {})[0] || [];
      const baselineData = Object.values(averageMarks[selectedIndex] || {})[0] || [];

      return (
        <LineGraph
          options={selOptions}
          specificData={specificData}
          baselineData={baselineData}
          specificDataLabel={props.studentInfo[4]}
          baselineDataLabel={`${props.studentInfo[5]} Average`}
          filterOptions={filterOptions}
          setFilterOptions={changeFilterOptions}
          labelId="termSelectLabel"
          compId="termSelectId"
          compLabel="Subject"
          compName="termSelect"
          xLabels={xLabels}
        />
      );
    } else if (
      props.acadienceScores &&
      (!props.studentInfo[5].includes("JK") ||
        !props.studentInfo[5].includes("SK"))
    ) {
      const acadienceMap = {
        3: [2, 2, 2, 2],
        4: [3, 2.5, 3, 2],
        5: [4, 2.5, 4, 2.5],
        6: [4.75, 3.5, 4.75, 3],
        7: [5.5, 4, 5.5, 3.75],
        8: [6, 4.25, 6, 4],
      };

      return (
        <TableCell
          align={props.insertBr ? "left" : "center"}
          key={`acadience-${index}`}
        >
          <p>
            {row + " "}
            {row != "n/a" &&
              acadienceMap[props.studentInfo[5][0]] &&
              (row >= acadienceMap[props.studentInfo[5][0]][index] ? (
                <div className="inline expectationsDiv expectationsMet">
                  Meeting Expectations{" "}
                  <div className="expectationsScore">
                    {acadienceMap[props.studentInfo[5][0]][index]}
                  </div>
                </div>
              ) : (
                <div className="inline expectationsDiv expectationsBelow ">
                  Below Expectations{" "}
                  <div className="expectationsScore">
                    {acadienceMap[props.studentInfo[5][0]][index]}
                  </div>
                </div>
              ))}
          </p>
        </TableCell>
      );
    } else {
      parts = row;
    }

    return (
      <TableCell
        align={props.insertBr ? "left" : "center"}
        key={`cell-${index}`}
      >
        <p>{parts}</p>
      </TableCell>
    );
  });

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>{createHeader}</TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            {createRowData}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
